import React, { useEffect, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControlLabel,
  ListItem,
  ListItemText,
  MenuItem,
  Paper,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { Group, Person } from '@mui/icons-material';
import { LoadingSpinner } from '../../core/LoadingSpinner';
import { useAlert } from '../../context/AlertContext';
import {
  IOpenFormulareDialogConfig,
  IOpenFormulareDialogEntry,
  useDeleteOpenFormulareDialogConfig,
  useGetOpenFormulareDialogs,
  useUpsertOpenFormulareDialogConfig,
} from '../../hooks/reactQuerys/OpenFormulareDialogQueries';
import { useGetLegalTransactionTypes } from '../../hooks/reactQuerys/LegalTransactionQueries';
import {
  useGetUserGroups,
  useGetUsers,
} from '../../hooks/reactQuerys/UserQueries';
import { selectableUserGroups } from '../../Interfaces/IUserGroup';

type DraftConfig = IOpenFormulareDialogConfig & {
  isDirty?: boolean;
};

const emptyDraft = (): DraftConfig => ({
  idLegalTransactionType: null,
  Notary_idUser: null,
  Employee_idUser: null,
  UserGroup_idUserGroup: null,
  isAutoImport: false,
});

export const OpenFormulareDialogsView: React.FC = () => {
  const { showAlert } = useAlert();
  const { ofDialogs, isLoadingOfDialogs } = useGetOpenFormulareDialogs();
  const { legalTransactionTypes } = useGetLegalTransactionTypes();
  const { users } = useGetUsers();
  const { userGroups } = useGetUserGroups();
  const { upsertOfDialogConfig, isUpsertingOfDialogConfig } =
    useUpsertOpenFormulareDialogConfig();
  const { deleteOfDialogConfig } = useDeleteOpenFormulareDialogConfig();

  const [drafts, setDrafts] = useState<Record<string, DraftConfig>>({});

  useEffect(() => {
    if (!ofDialogs) return;
    const next: Record<string, DraftConfig> = {};
    for (const d of ofDialogs.dialogs) {
      next[d.DialogId] = d.config
        ? { ...(d.config as DraftConfig) }
        : emptyDraft();
    }
    setDrafts(next);
  }, [ofDialogs]);

  if (isLoadingOfDialogs) return <LoadingSpinner />;
  if (!ofDialogs) {
    return (
      <Alert severity='error'>
        Fehler beim Laden der OpenFormulare-Dialoge.
      </Alert>
    );
  }

  const notaryOptions = (users ?? []).filter(
    u => Boolean(u.isNotary) && !u.isBlocked
  );

  type AssigneeOption =
    | { type: 'Mitarbeiter'; id: number; name: string }
    | { type: 'Gruppe'; id: number; name: string };

  const assigneeOptions: AssigneeOption[] = [
    ...(users ?? [])
      .filter(u => !u.isNotary && !u.isBlocked)
      .map<AssigneeOption>(u => ({
        type: 'Mitarbeiter',
        id: u.idUser,
        name: `${u.FirstName ?? ''} ${u.LastName ?? ''}`.trim(),
      })),
    ...selectableUserGroups(userGroups).map<AssigneeOption>(g => ({
      type: 'Gruppe',
      id: g.idUserGroup,
      name: g.UserGroup,
    })),
  ];

  const updateDraft = (dialogId: string, patch: Partial<DraftConfig>) => {
    setDrafts(prev => ({
      ...prev,
      [dialogId]: {
        ...(prev[dialogId] ?? emptyDraft()),
        ...patch,
        isDirty: true,
      },
    }));
  };

  const handleSave = async (entry: IOpenFormulareDialogEntry) => {
    const draft = drafts[entry.DialogId] ?? emptyDraft();
    if (
      draft.isAutoImport &&
      (!draft.idLegalTransactionType || !draft.Notary_idUser)
    ) {
      showAlert({
        text: 'Für den automatischen Import sind Vorgangsart und Notar Pflicht.',
        severity: 'warning',
      });
      return;
    }
    try {
      await upsertOfDialogConfig({
        dialogId: entry.DialogId,
        body: {
          DialogTitle: entry.DialogTitle,
          idLegalTransactionType: draft.idLegalTransactionType,
          Notary_idUser: draft.Notary_idUser,
          Employee_idUser: draft.Employee_idUser,
          UserGroup_idUserGroup: draft.UserGroup_idUserGroup,
          isAutoImport: !!draft.isAutoImport,
        },
      });
      showAlert({
        text: 'Dialog-Konfiguration gespeichert.',
        severity: 'success',
      });
    } catch (e) {
      console.error(e);
      showAlert({ text: 'Speichern fehlgeschlagen.', severity: 'error' });
    }
  };

  const handleReset = async (entry: IOpenFormulareDialogEntry) => {
    if (!entry.config) {
      setDrafts(prev => ({ ...prev, [entry.DialogId]: emptyDraft() }));
      return;
    }
    try {
      await deleteOfDialogConfig(entry.DialogId);
      showAlert({ text: 'Konfiguration zurückgesetzt.', severity: 'success' });
    } catch (e) {
      console.error(e);
      showAlert({
        text: 'Zurücksetzen fehlgeschlagen.',
        severity: 'error',
      });
    }
  };

  return (
    <Box>
      <Typography variant='h4' sx={{ mb: 1 }}>
        Dialoge
      </Typography>
      <Typography variant='body2' color='text.secondary' sx={{ mb: 2 }}>
        Konfiguration der von OpenFormulare bereitgestellten Dialoge. Alle
        Felder sind optional — gesetzte Werte werden beim Übernahmen-Schritt
        (DialogInbox) vorausgewählt. Für den automatischen Import sind
        Vorgangsart und Notar Pflicht.
      </Typography>

      {!ofDialogs.ofReachable && (
        <Alert severity='warning' sx={{ mb: 2 }}>
          OpenFormulare ist nicht erreichbar oder nicht konfiguriert. Sie sehen
          nur bereits lokal gespeicherte Konfigurationen.
        </Alert>
      )}

      {ofDialogs.dialogs.length === 0 ? (
        <Alert severity='info'>
          Keine Dialoge in der verbundenen OpenFormulare-Instanz gefunden.
        </Alert>
      ) : (
        <Stack spacing={2}>
          {ofDialogs.dialogs.map(entry => {
            const draft = drafts[entry.DialogId] ?? emptyDraft();
            const assigneeValue: AssigneeOption | null = draft.Employee_idUser
              ? (assigneeOptions.find(
                  o =>
                    o.type === 'Mitarbeiter' && o.id === draft.Employee_idUser
                ) ?? null)
              : draft.UserGroup_idUserGroup
                ? (assigneeOptions.find(
                    o =>
                      o.type === 'Gruppe' &&
                      o.id === draft.UserGroup_idUserGroup
                  ) ?? null)
                : null;
            return (
              <Paper
                key={entry.DialogId}
                variant='outlined'
                sx={{ p: 2, opacity: entry.isOrphan ? 0.7 : 1 }}
              >
                <Stack
                  direction='row'
                  alignItems='center'
                  justifyContent='space-between'
                  spacing={1}
                  sx={{ mb: 1 }}
                >
                  <Box>
                    <Stack
                      direction='row'
                      spacing={1}
                      alignItems='center'
                      sx={{ mb: 0.5 }}
                    >
                      <Typography variant='subtitle1'>
                        {entry.DialogTitle}
                      </Typography>
                      {entry.category && entry.category !== '—' && (
                        <Chip
                          size='small'
                          label={entry.category}
                          variant='outlined'
                        />
                      )}
                      {!entry.isActive && (
                        <Chip
                          size='small'
                          label='In OF deaktiviert'
                          color='warning'
                          variant='outlined'
                        />
                      )}
                      {entry.isOrphan && (
                        <Chip
                          size='small'
                          label='Orphan: kein Dialog in OF'
                          color='error'
                          variant='outlined'
                        />
                      )}
                      {draft.isAutoImport && draft.isAutoImportComplete && (
                        <Chip
                          size='small'
                          label='Auto-Import aktiv'
                          color='success'
                        />
                      )}
                    </Stack>
                    {entry.description && (
                      <Typography
                        variant='caption'
                        color='text.secondary'
                        sx={{ display: 'block' }}
                      >
                        {entry.description}
                      </Typography>
                    )}
                    <Typography variant='caption' color='text.secondary'>
                      Dialog-ID: <code>{entry.DialogId}</code>
                    </Typography>
                  </Box>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={!!draft.isAutoImport}
                        onChange={(_, checked) =>
                          updateDraft(entry.DialogId, { isAutoImport: checked })
                        }
                      />
                    }
                    label='Automatisch importieren'
                  />
                </Stack>

                <Table size='small'>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ width: '30%' }}>Vorgangsart</TableCell>
                      <TableCell sx={{ width: '30%' }}>Notar</TableCell>
                      <TableCell sx={{ width: '30%' }}>
                        Mitarbeiter / Gruppe
                      </TableCell>
                      <TableCell sx={{ width: '10%' }} />
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    <TableRow>
                      <TableCell>
                        <Autocomplete
                          size='small'
                          fullWidth
                          options={legalTransactionTypes ?? []}
                          getOptionLabel={o => o.LegalTransactionType ?? ''}
                          isOptionEqualToValue={(opt, val) =>
                            opt.idLegalTransactionType ===
                            val.idLegalTransactionType
                          }
                          value={
                            (legalTransactionTypes ?? []).find(
                              t =>
                                t.idLegalTransactionType ===
                                draft.idLegalTransactionType
                            ) ?? null
                          }
                          onChange={(_e, v) =>
                            updateDraft(entry.DialogId, {
                              idLegalTransactionType:
                                v?.idLegalTransactionType ?? null,
                            })
                          }
                          renderInput={params => (
                            <TextField
                              {...params}
                              placeholder={
                                draft.isAutoImport ? 'Pflicht *' : 'optional'
                              }
                              error={
                                draft.isAutoImport &&
                                !draft.idLegalTransactionType
                              }
                            />
                          )}
                        />
                      </TableCell>
                      <TableCell>
                        <TextField
                          size='small'
                          fullWidth
                          select
                          value={draft.Notary_idUser ?? ''}
                          onChange={e =>
                            updateDraft(entry.DialogId, {
                              Notary_idUser:
                                e.target.value === ''
                                  ? null
                                  : Number(e.target.value),
                            })
                          }
                          placeholder={
                            draft.isAutoImport ? 'Pflicht *' : 'optional'
                          }
                          error={draft.isAutoImport && !draft.Notary_idUser}
                        >
                          <MenuItem value=''>
                            <em>–</em>
                          </MenuItem>
                          {notaryOptions.map(u => (
                            <MenuItem key={u.idUser} value={u.idUser}>
                              {u.FirstName} {u.LastName}
                            </MenuItem>
                          ))}
                        </TextField>
                      </TableCell>
                      <TableCell>
                        <Autocomplete
                          size='small'
                          fullWidth
                          options={assigneeOptions}
                          groupBy={o => o.type}
                          getOptionLabel={o => o.name}
                          value={assigneeValue}
                          onChange={(_e, v) => {
                            if (!v) {
                              updateDraft(entry.DialogId, {
                                Employee_idUser: null,
                                UserGroup_idUserGroup: null,
                              });
                            } else if (v.type === 'Mitarbeiter') {
                              updateDraft(entry.DialogId, {
                                Employee_idUser: v.id,
                                UserGroup_idUserGroup: null,
                              });
                            } else {
                              updateDraft(entry.DialogId, {
                                Employee_idUser: null,
                                UserGroup_idUserGroup: v.id,
                              });
                            }
                          }}
                          renderOption={(props, o) => (
                            <ListItem {...props} key={`${o.type}-${o.id}`}>
                              <ListItemText
                                primary={o.name}
                                secondary={o.type}
                              />
                            </ListItem>
                          )}
                          renderInput={params => (
                            <TextField
                              {...params}
                              placeholder='optional'
                              InputProps={{
                                ...params.InputProps,
                                startAdornment:
                                  assigneeValue?.type === 'Gruppe' ? (
                                    <Group fontSize='small' sx={{ mr: 1 }} />
                                  ) : (
                                    <Person fontSize='small' sx={{ mr: 1 }} />
                                  ),
                              }}
                            />
                          )}
                        />
                      </TableCell>
                      <TableCell align='right'>
                        <Stack
                          direction='row'
                          spacing={1}
                          justifyContent='flex-end'
                        >
                          <Button
                            size='small'
                            variant='outlined'
                            onClick={() => handleReset(entry)}
                          >
                            Zurücksetzen
                          </Button>
                          <Button
                            size='small'
                            variant='contained'
                            disabled={
                              isUpsertingOfDialogConfig || !draft.isDirty
                            }
                            onClick={() => handleSave(entry)}
                          >
                            {isUpsertingOfDialogConfig ? (
                              <CircularProgress size={16} />
                            ) : (
                              'Speichern'
                            )}
                          </Button>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </Paper>
            );
          })}
        </Stack>
      )}
    </Box>
  );
};
